import { useState } from 'react';
import { createInteraction } from '../lib/api';
import { useApiMutation } from '../hooks/useApi';
import type { Interaction } from '../types';

interface InteractionHistoryProps {
  interactions: Interaction[];
  customerId: string;
  customerEmail: string;
  onInteractionCreated: () => void;
}

const interactionTypeColors: Record<string, string> = {
  call: 'badge-info',
  email: 'badge-info',
  chat: 'badge-info',
  note: 'badge-gray',
  action: 'badge-warning',
};

const statusColors: Record<string, string> = {
  open: 'badge-warning',
  resolved: 'badge-success',
  escalated: 'badge-danger',
  pending: 'badge-gray',
};

export default function InteractionHistory({
  interactions,
  customerId,
  customerEmail,
  onInteractionCreated,
}: InteractionHistoryProps) {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    interactionType: 'note',
    channel: 'portal',
    summary: '',
    status: 'open',
  });

  const { loading, error, execute } = useApiMutation(
    (data: typeof formData) =>
      createInteraction(customerId, { ...data, customerEmail }),
    {
      onSuccess: () => {
        setFormData({ interactionType: 'note', channel: 'portal', summary: '', status: 'open' });
        setShowForm(false);
        onInteractionCreated();
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    execute(formData);
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-gray-900">Interaction History</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn-secondary text-sm"
        >
          {showForm ? 'Cancel' : 'New Interaction'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="label">Type</label>
              <select
                className="input"
                value={formData.interactionType}
                onChange={(e) => setFormData({ ...formData, interactionType: e.target.value })}
              >
                <option value="call">Call</option>
                <option value="email">Email</option>
                <option value="chat">Chat</option>
                <option value="note">Note</option>
              </select>
            </div>
            <div>
              <label className="label">Channel</label>
              <select
                className="input"
                value={formData.channel}
                onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
              >
                <option value="phone">Phone</option>
                <option value="email">Email</option>
                <option value="portal">Portal</option>
                <option value="chat">Chat</option>
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="label">Summary</label>
            <textarea
              className="input"
              rows={3}
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              placeholder="Describe the interaction..."
              required
            />
          </div>

          <div className="mb-4">
            <label className="label">Status</label>
            <select
              className="input"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="open">Open</option>
              <option value="resolved">Resolved</option>
              <option value="escalated">Escalated</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          {error && (
            <p className="text-sm text-red-600 mb-4">{error}</p>
          )}

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Creating...' : 'Create Interaction'}
          </button>
        </form>
      )}

      {interactions.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">No interactions recorded.</p>
      ) : (
        <div className="space-y-4">
          {interactions.map((interaction) => (
            <div
              key={interaction.id}
              className="border-l-4 border-gray-200 pl-4 py-2 hover:border-primary-400 transition-colors"
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center space-x-2">
                  <span className={interactionTypeColors[interaction.interaction_type] || 'badge-gray'}>
                    {interaction.interaction_type}
                  </span>
                  <span className={statusColors[interaction.status] || 'badge-gray'}>
                    {interaction.status}
                  </span>
                  {interaction.channel && (
                    <span className="text-xs text-gray-500">via {interaction.channel}</span>
                  )}
                </div>
                <span className="text-xs text-gray-500">
                  {new Date(interaction.created_at).toLocaleString()}
                </span>
              </div>
              <p className="text-sm text-gray-700">{interaction.summary}</p>
              <p className="text-xs text-gray-500 mt-1">
                by {interaction.agent_name || interaction.agent_email}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
